import { Bell, Shield, Zap } from "lucide-react";
import SettingRow from "@/components/setting-row.component";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";

import { useProtectionSummary } from "@/api/protection/queries";
import { useSetProtectionActivation } from "@/api/protection/mutations";
import { PROTECTION_KEY_TO_RISK_TYPE } from "@/api/protection/types";
import { Switch } from "@/components/switch";

import { ProtectionKey } from "./enums/protection-key.enum";
import { SettingRowProps } from "@/components/setting-row.component";
import OvalBadge from "@/components/oval-badge.component";
import SectionTitle from "@/components/section-title.component";

// Élision de la préposition devant une voyelle ("de grêle" → "d'échaudage").
// On passe un `context` i18next : la clé `_vowel` est utilisée si elle existe
// dans la langue courante, sinon i18next retombe sur la clé de base.
const VOWELS = "aàâäeéèêëiîïoôöuùûüy";
const startsWithVowel = (value: string) =>
  VOWELS.includes(value.trim().charAt(0).toLowerCase());

function ComingSoonRow(props: Omit<SettingRowProps, "trailing" | "muted">) {
  const { t: translator } = useTranslation();
  return (
    <SettingRow
      {...props}
      muted
      trailing={
        <OvalBadge
          textToDisplay={translator("protection.comingSoon")}
          primary={false}
          disabled={true}
        />
      }
    />
  );
}

function ActivationProtectionToggle({
  areaId,
  protectionKey,
}: {
  areaId: string;
  protectionKey: ProtectionKey;
}) {
  const { t } = useTranslation();
  const riskType = PROTECTION_KEY_TO_RISK_TYPE[protectionKey];

  const { data, isLoading } = useProtectionSummary({ areaId, riskType });
  const isArmed = Boolean(data?.pages[0]?.activeSince);

  const { mutate, isPending } = useSetProtectionActivation({
    areaId,
    riskType,
  });

  return (
    <SettingRow
      icon={<Shield css={tw`w-5 h-5`} style={{ color: "#166534" }} />}
      label={
        isArmed ? t("protection.armedSystem") : t("protection.disarmedSystem")
      }
      description={t("protection.armedSystemDesc")}
      trailing={
        <Switch
          checked={isArmed}
          disabled={isLoading || isPending}
          onCheckedChange={(next) => mutate(next)}
        />
      }
    />
  );
}

export function ProtectionSettingsCard({
  areaId,
  protectionKey,
}: {
  areaId: string;
  protectionKey: ProtectionKey;
}) {
  const { t } = useTranslation();
  const name = t(`protection.${protectionKey}`);

  return (
    <div css={tw`flex flex-col`}>
      <SectionTitle>{t("protection.settings")}</SectionTitle>
      <div
        css={tw`bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100`}
      >
        <ActivationProtectionToggle
          areaId={areaId}
          protectionKey={protectionKey}
        />
        <ComingSoonRow
          icon={<Bell css={tw`w-5 h-5`} style={{ color: "#166534" }} />}
          label={t("protection.notifications")}
          description={t(
            startsWithVowel(name)
              ? "protection.notificationsDesc_vowel"
              : "protection.notificationsDesc",
            { name: name.toLowerCase() }
          )}
        />
        <ComingSoonRow
          icon={<Zap css={tw`w-5 h-5`} style={{ color: "#166534" }} />}
          label={t("protection.automaticActions")}
          description={t("protection.automaticActionsDesc")}
        />
      </div>
    </div>
  );
}
